import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LockKey, ShieldCheck } from "@phosphor-icons/react";

/**
 * Blocking modal shown when a user's role requires PIN (kasir/superadmin/admin)
 * but they haven't set one yet. Currently: kasir role is REQUIRED to set PIN.
 */
export default function ForcePinSetup() {
  const { user, refresh } = useAuth();
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [saving, setSaving] = useState(false);

  // Only enforce for kasir role (and can be extended to admin/superadmin later)
  const needsPin = user && user.role === "kasir" && user.has_pin === false;
  if (!needsPin) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) { toast.error("PIN harus 6 digit angka"); return; }
    if (pin !== pin2) { toast.error("Konfirmasi PIN tidak cocok"); return; }
    setSaving(true);
    try {
      await api.post("/auth/set-pin", { pin });
      toast.success("PIN tersimpan. Login dari HP jadi lebih cepat.");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal set PIN"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-pin-modal">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-[#00558F]/10 flex items-center justify-center"><ShieldCheck size={26} className="text-[#00558F]" weight="duotone"/></div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">Set PIN Wajib</div>
            <div className="text-xs text-slate-500">Untuk akun Kasir, PIN 6-digit diperlukan</div>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 mb-4">
          Login dari HP nanti tinggal ketik PIN 6 digit tanpa email + password panjang. Data tetap aman.
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">PIN Baru (6 digit)</label>
            <input data-testid="force-pin-input" type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,""))} className="w-full px-4 py-3 border border-slate-300 rounded-md text-center font-mono-tab text-2xl tracking-[0.5em] focus:outline-none focus:border-[#00558F] focus:ring-2 focus:ring-[#00558F]/20 transition-colors" placeholder="••••••" autoFocus/>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Konfirmasi PIN</label>
            <input data-testid="force-pin-confirm" type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={pin2} onChange={e=>setPin2(e.target.value.replace(/\D/g,""))} className="w-full px-4 py-3 border border-slate-300 rounded-md text-center font-mono-tab text-2xl tracking-[0.5em] focus:outline-none focus:border-[#00558F] focus:ring-2 focus:ring-[#00558F]/20 transition-colors" placeholder="••••••"/>
          </div>
          <button data-testid="force-pin-submit" type="submit" disabled={saving} className="w-full py-3 bg-[#00558F] hover:bg-[#003E6B] text-white font-semibold rounded-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            <LockKey size={16}/> {saving?"Menyimpan...":"Simpan PIN & Lanjut"}
          </button>
        </form>
      </div>
    </div>
  );
}
